/**
 * TabCadenceEncaissements.js
 * Triangle de cadence : survenus en (date_accident) × encaissés en.
 * Réutilise le même composant BlocTable que TabCadence.js.
 */
import React, { useState } from 'react';
import { Spin, Empty } from 'antd';
import { LoadingOutlined, CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import { ecrireExcel } from './excelUtils';
import { C } from './TableauReporting';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { localeIntl } from '../../../i18n/format';

const tx = (cle, options) => i18n.t(`statistiques:reporting.${cle}`, options);

const fmtN = (v) => v ? new Intl.NumberFormat(localeIntl()).format(v) : '-';
const fmtM = (v) => v ? new Intl.NumberFormat(localeIntl()).format(Number(v)) : '-';
const fmtPct = (v) => v ? (v * 100).toFixed(1) + '%' : '-';

const BG_DIAG = ['#dcfce7','#d1fae5','#bbf7d0','#86efac','#4ade80'];

const bgCell = (surv, enc, periodes) => {
  if (surv === -1 || enc === -1) return '#f1f5f9';
  const iSurv = periodes.indexOf(surv);
  const iEnc  = periodes.indexOf(enc);
  if (iSurv === -1 || iEnc === -1) return '#fff';
  const diag = iEnc - iSurv;
  if (diag < 0) return '#fef9c3';
  return BG_DIAG[Math.min(diag, BG_DIAG.length - 1)];
};

const labelP = (a, anneeMin) => a === -1 ? i18n.t('statistiques:commun.etAnterieures', { annee: anneeMin - 1 }) : String(a);

const TH = (bg = '#1e293b', w) => ({
  background: bg, color: '#fff', padding: '6px 8px', textAlign: 'center',
  fontSize: 11, fontWeight: 700, border: '1px solid #334155', whiteSpace: 'nowrap',
  ...(w ? { minWidth: w } : {}),
});
const TD = (bg = '#fff', bold = false) => ({
  padding: '4px 7px', textAlign: 'right', fontSize: 11,
  border: `1px solid ${C.border}`, background: bg,
  fontWeight: bold ? 700 : 400, color: bold ? C.ink : '#334155',
  fontFamily: 'var(--font-main)',
});
const TDL = (bg = '#fff', bold = false) => ({
  ...TD(bg, bold), textAlign: 'left', fontFamily: "'DM Sans', sans-serif",
  paddingLeft: 10, minWidth: 110,
});

export function exportCadenceEncaissements(data) {
  if (!data) return;
  const anneeMin = data.anneeRef - 3;

  const blocToAoa = (bloc) => {
    const rows = [['', bloc.label]];
    const header = [tx('excel.survenusEn'),
      ...data.anneesColonnes.map(a => labelP(a, anneeMin)),
      tx('excel.totalNb'), tx('excel.totalMt'), tx('cadence.dec'), tx('cadence.taux')];
    rows.push(header);
    for (const ligne of bloc.lignes) {
      const nbRow  = [labelP(ligne.anneeSurvenance, anneeMin), tx('excel.nbre')];
      const mtRow  = ['', tx('excel.montant')];
      for (const a of data.anneesColonnes) {
        const cell = ligne.cellules[a] || { nb: 0, montant: 0 };
        nbRow.push(cell.nb); mtRow.push(Number(cell.montant));
      }
      nbRow.push(ligne.nbTotal, '', ligne.sinistresDeClares,
                 ligne.sinistresDeClares ? ligne.nbTotal / ligne.sinistresDeClares : '');
      mtRow.push('', ligne.montantTotal, '', '');
      rows.push(nbRow, mtRow);
    }
    rows.push([]);
    return rows;
  };

  const allAoa = [
    ['BNCB-TOGO', tx('excel.titreCadenceEncaissements'), tx('excel.anneeAnnee', { annee: data.anneeRef })],
    [tx('excel.survenanceEncaissement')], [],
    ...blocToAoa(data.total),
    [tx('excel.parPays')],
    ...data.parPays.flatMap(b => blocToAoa(b)),
    [tx('excel.parCompagnie')],
    ...data.parCompagnie.flatMap(b => blocToAoa(b)),
  ];

  ecrireExcel([{
    name: tx('excel.feuilles.cadenceEncaissements'),
    aoa: allAoa,
    colWidths: [16, 8, ...Array(data.anneesColonnes.length * 2).fill(10), 8, 14, 8, 8],
  }], `Cadence_Encaissements_${data.anneeRef}`);
  toast.success(tx('excel.succes.cadenceEncaissements'));
}

export default function TabCadenceEncaissements({ data, loading }) {
  const { t } = useTranslation('statistiques');
  const [expanded, setExpanded] = useState({ total: true });
  const toggle = (key) => setExpanded(s => ({ ...s, [key]: !s[key] }));

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
      <Spin indicator={<LoadingOutlined style={{ fontSize: 28, color: C.green }} />} />
    </div>
  );
  if (!data) return <Empty description={t('commun.aucuneDonnee')} style={{ padding: 40 }} />;

  const anneeMin = data.anneeRef - 3;
  const blocs = [
    { key: 'total', label: 'TOTAL', bloc: data.total },
    ...data.parPays.map((b, i) => ({ key: `pays-${i}`, label: b.label, bloc: b })),
    ...data.parCompagnie.map((b, i) => ({ key: `comp-${i}`, label: b.label, bloc: b })),
  ];

  return (
    <div>
      <style>{`
        .cad-enc-table { border-collapse: collapse; width: 100%; }
        .cad-enc-table tr:hover td { filter: brightness(0.96); }
        .cad-enc-section { margin-bottom: 12px; border-radius: 10px; overflow: hidden;
          border: 1px solid ${C.border}; }
      `}</style>

      {/* Légende */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center',
        marginBottom: 16, fontSize: 11, color: C.muted }}>
        <span style={{ fontWeight: 600, color: C.ink }}>{t('reporting.cadence.delaiEncaissement')}</span>
        {['memeAnnee', 'plus1', 'plus2', 'plus3', 'plus4'].map((cle) => t(`reporting.cadence.diagonales.${cle}`)).map((l, i) => (
          <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 14, height: 14, borderRadius: 3,
              background: BG_DIAG[Math.min(i, BG_DIAG.length-1)],
              border: `1px solid ${C.border}`, display: 'inline-block' }} />
            {l}
          </span>
        ))}
      </div>

      {blocs.map(({ key, label, bloc }) => (
        <div key={key} className="cad-enc-section">
          <div onClick={() => toggle(key)}
            style={{ background: '#1e293b', color: '#fff', padding: '8px 14px',
              display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            {expanded[key] ? <CaretDownOutlined /> : <CaretRightOutlined />}
            <span style={{ fontWeight: 700, fontSize: 12 }}>{label}</span>
          </div>
          {expanded[key] && (
            <div style={{ overflowX: 'auto' }}>
              <table className="cad-enc-table">
                <thead>
                  <tr>
                    <th colSpan={2} style={TH('#334155', 110)}>{t('reporting.cadence.survenusEn')}</th>
                    {data.anneesColonnes.map(a => (
                      <th key={a} colSpan={2} style={TH(a === data.anneeRef ? '#166534' : '#1e3a5f')}>
                        {t('reporting.cadence.encaissesEn', { annee: labelP(a, anneeMin) })}
                      </th>
                    ))}
                    <th colSpan={2} style={TH('#4a1942')}>TOTAL</th>
                    <th style={TH('#334155')}>{t('reporting.cadence.dec')}</th>
                    <th style={TH('#334155')}>{t('reporting.cadence.taux')}</th>
                  </tr>
                  <tr>
                    <th colSpan={2} style={{ ...TH('#475569'), fontSize: 10 }}>{t('reporting.cadence.annee')}</th>
                    {data.anneesColonnes.map(a => (
                      <React.Fragment key={a}>
                        <th style={{ ...TH(a === data.anneeRef ? '#1f4035' : '#162d47'), fontSize: 10 }}>{t('reporting.tableau.nb')}</th>
                        <th style={{ ...TH(a === data.anneeRef ? '#1f4035' : '#162d47'), fontSize: 10 }}>{t('reporting.cadence.mtFcfa')}</th>
                      </React.Fragment>
                    ))}
                    <th style={{ ...TH('#3b1238'), fontSize: 10 }}>{t('reporting.tableau.nb')}</th>
                    <th style={{ ...TH('#3b1238'), fontSize: 10 }}>{t('reporting.tableau.mt')}</th>
                    <th style={{ ...TH('#475569'), fontSize: 10 }}>{t('reporting.tableau.nb')}</th>
                    <th style={{ ...TH('#475569'), fontSize: 10 }}>%</th>
                  </tr>
                </thead>
                <tbody>
                  {bloc.lignes.map((ligne, i) => {
                    const isAnt = ligne.anneeSurvenance === -1;
                    const rowBg = isAnt ? '#f8fafc' : (i % 2 === 0 ? '#fff' : '#fafafa');
                    return (
                      <tr key={i}>
                        <td colSpan={2} style={TDL(rowBg, isAnt)}>
                          {labelP(ligne.anneeSurvenance, anneeMin)}
                        </td>
                        {data.anneesColonnes.map(enc => {
                          const cell = ligne.cellules[enc] || { nb: 0, montant: 0 };
                          const bg   = bgCell(ligne.anneeSurvenance, enc, data.anneesColonnes);
                          return (
                            <React.Fragment key={enc}>
                              <td style={TD(bg)}>
                                {cell.nb > 0 ? fmtN(cell.nb) : <span style={{ color: C.muted }}>-</span>}
                              </td>
                              <td style={{ ...TD(bg), minWidth: 100 }}>
                                {Number(cell.montant) > 0 ? fmtM(cell.montant)
                                  : <span style={{ color: C.muted }}>-</span>}
                              </td>
                            </React.Fragment>
                          );
                        })}
                        <td style={TD('#f0fdf4', true)}>{ligne.nbTotal > 0 ? fmtN(ligne.nbTotal) : '-'}</td>
                        <td style={{ ...TD('#dcfce7', true), minWidth: 110 }}>
                          {Number(ligne.montantTotal) > 0 ? fmtM(ligne.montantTotal) : '-'}
                        </td>
                        <td style={TD('#f8fafc')}>
                          {ligne.sinistresDeClares > 0 ? fmtN(ligne.sinistresDeClares) : '-'}
                        </td>
                        <td style={{ ...TD('#f8fafc'), fontWeight: 600,
                          color: ligne.tauxPaye > 0.8 ? '#16a34a'
                               : ligne.tauxPaye > 0.4 ? '#d97706' : C.ink }}>
                          {ligne.sinistresDeClares > 0 ? fmtPct(ligne.tauxPaye) : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
